
import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import * as echarts from 'echarts';
import { applyRoleVisibilityToOption } from '../../utils/chart-role-visibility.utils';
// import { ConfigService } from '../../shared-services/config.service';
// import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ConfigService} from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { ChartFilterHandlerService } from '@shared-lib/shared-services/chart-filter-handler.service';
import { ThemeService } from '@shared-lib/shared-services/theme.service';
import { ResponsiveLayoutService } from '@shared-lib/shared-services/responsive-layout.service';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { CapitalCasePipe } from '../../pipes/capital-case.pipe';

export interface StackedBarSeries {
  name: string;
  data: number[];
  color?: string;
}

@Component({
  selector: 'app-stacked-100-bar',
  standalone: true,
  imports: [CommonModule, CapitalCasePipe],
  templateUrl: './stacked-barchart-hundred-percentage.component.html',
  styleUrls: ['./stacked-barchart-hundred-percentage.component.scss']
})
export class Stacked100BarComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('chartContainer', { static: false })
  chartContainer!: ElementRef<HTMLDivElement>;
  @Input() data!: any; // chart configuration passed by renderer
  @Input() categories: string[] = [];
  @Input() series: StackedBarSeries[] = [];
  @Input() showLegend = true;

  chartTitle: string = '';
  sliderIndex: number = 0;
  allCategories: string[] = [];
  allSeries: StackedBarSeries[] = [];

  // scrollbar / slider settings
  maxVisibleCategories = 7;
  visibleHeight = 250;
  // Scrollbar drag state
  protected isScrollbarDragging = false;
  protected scrollbarStartY = 0;
  protected scrollStartOffset = 0;
  protected dragHandler: ((e: MouseEvent) => void) | null = null;
  protected upHandler: (() => void) | null = null;
  // expose Math for template expressions
  readonly Math = Math;

  protected filterSub?: Subscription;
  protected reflowSub?: Subscription;
  protected unSubscribe$ = new Subject<void>();
  protected selectedSeriesName: string | null = null;
  protected selectedCategory: string | null = null;

  protected chart!: echarts.ECharts;
  private resizeObserver!: ResizeObserver;
  private themeService = inject(ThemeService);
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeSub?: Subscription;
  protected lastRawBar: any[] = [];
  protected exclusiveLegendName: string | null = null;

  constructor(
    protected configService: ConfigService,
    protected globalFilterService: GlobalFilterService,
    protected chartFilterHandler: ChartFilterHandlerService
  ) {}

  ngAfterViewInit(): void {
    console.log('Stacked100Bar: ngAfterViewInit, chartContainer element:', this.chartContainer.nativeElement);
    
    this.resizeObserver = new ResizeObserver(() => {
      this.syncVisibleHeightFromContainer();
      this.chart?.resize();
    });

    if (!this.chartContainer) {
      console.warn('Stacked100Bar: chartContainer not found, delaying initialization');
      setTimeout(() => this.initializeChart(), 100);
      return;
    }
    this.syncVisibleHeightFromConfig();
    this.maxVisibleCategories = this.data?.chartConfig?.maxVisibleCategories || this.maxVisibleCategories;
    this.initializeChart();

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.syncVisibleHeightFromConfig();
      this.chart?.resize();
    });
  }

  protected initializeChart(): void {
    if (!this.chartContainer) {
      console.error('Stacked100Bar: chartContainer still not available');
      return;
    }    this.chart = echarts.init(this.chartContainer.nativeElement, 'dvm');
    this.resizeObserver.observe(this.chartContainer.nativeElement);
    console.log('Stacked100Bar: Chart instance initialized:', this.chart);
    this.filterSub = this.globalFilterService.filter$
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((filters: any) => {
        if (!this.chartFilterHandler.shouldReload(filters, this.data?.widgetId)) {
          return;
        }
        console.log('Stacked100Bar: Filter changed:', filters);
        this.loadAndRenderChart(filters);
      });

    // Re-render with fresh theme tokens when the user switches theme at runtime.
    // Dispose + re-init so ECharts picks up the updated 'dvm' registered theme.
    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.chart || !this.categories?.length) return;
      const el = this.chartContainer?.nativeElement;
      if (!el) return;
      this.chart.dispose();
      this.chart = echarts.init(el, 'dvm');
      this.updateChart();
    });

    this.loadAndRenderChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.chart) {
      // If external `data` input changed, reload
      if (changes['data']) {
        this.loadAndRenderChart();
      } else {
        this.updateChart();
      }
    }
  }

  ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
    this.filterSub?.unsubscribe();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    this.chart?.dispose();
    this.resizeObserver?.disconnect();
  }

  protected loadAndRenderChart(filters?: any): void {
    console.log('Stacked100Bar: loadAndRenderChart called, data=', this.data, 'filters=', filters);

    if (!this.data || !this.data.apiUrl) {
      // If no apiUrl provided, but categories/series exist, render directly
      this.updateChart();
      this.chart.hideLoading();
      return;
    }

    this.chart.showLoading();
    this.configService.getConfig(this.data.apiUrl, this.data, filters).subscribe({
      next: (res: any) => {
        console.log('Stacked100Bar: config response=', res);
        const payload = res?.data || res;
        
        // Extract title from response if available
        if (payload?.title) {
          this.chartTitle = payload.title;
        }
        
        // Handle response with bar property (raw data array)
        if (payload?.bar && Array.isArray(payload.bar)) {
          console.log('Stacked100Bar: transforming bar data...');
          // preserve raw rows for tooltip quantity values
          this.lastRawBar = payload.bar;
          const transformed = this.transformData(payload.bar, this.data.categoryKey, this.data.seriesKey, this.data.valueKey);
          this.allCategories = transformed.category;
          this.allSeries = transformed.series;
          this.sliderIndex = 0;
          // ensure scrollbar config and visible height are applied
          this.maxVisibleCategories = this.data?.chartConfig?.maxVisibleCategories || this.maxVisibleCategories;
          this.syncVisibleHeightFromConfig();
          try { if (this.chart) this.chart.resize(); } catch (e) {}
          this.updateSlicedData();
        } else if (payload?.categories && payload?.series) {
          // Handle pre-formatted response
          this.allCategories = payload.categories;
          this.allSeries = payload.series;
          this.sliderIndex = 0;
          this.maxVisibleCategories = this.data?.chartConfig?.maxVisibleCategories || this.maxVisibleCategories;
          this.syncVisibleHeightFromConfig();
          try { if (this.chart) this.chart.resize(); } catch (e) {}
          this.updateSlicedData();
        } else {
          this.allCategories = [];
          this.allSeries = [];
          this.categories = [];
          this.series = [];
        }
        
        this.updateChart();
        this.chart.hideLoading();
      },
      error: (err: any) => {
        console.error('Stacked100Bar: config error', err);
        this.chart.hideLoading();
      }
    });
  }

  protected updateChart(): void {
    console.log('Stacked100Bar: updateChart called with categories=', this.categories, 'series=', this.series);
    if (!this.categories.length || !this.series.length) {
      console.warn('Stacked100Bar: No categories or series data, clearing chart');
      this.chart.clear();
      return;
    }

    const theme = this.themeService.resolve(this.data?.themeOverrides);

    console.log('Stacked100Bar: Creating chart option...');
    // Compact number formatter for K/M display
    const formatCompact = (n: number) => {
      if (n === null || n === undefined || !isFinite(n)) return '';
      const abs = Math.abs(n);
      if (abs >= 1000000) {
        const val = Math.round(n / 100000) / 10; // one decimal precision
        return `${val % 1 === 0 ? val.toFixed(0) : val.toFixed(1)}M`;
      }
      if (abs >= 1000) {
        const val = Math.round(n / 100) / 10; // one decimal precision
        return `${val % 1 === 0 ? val.toFixed(0) : val.toFixed(1)}K`;
      }
      return n.toLocaleString('en-US');
    };
    const fmtNumber = (v: any) => (typeof v === 'number' ? formatCompact(v) : v);
    // compute legend layout similar to clustered/mixed charts
    const chartWidth = (this.chart && (this.chart.getWidth ? this.chart.getWidth() : 0)) || (this.chartContainer?.nativeElement?.clientWidth) || 400;
    const avgCharWidth = 7;
    const itemWidthPx = 12; // match mixed chart color box size
    const itemGap = 16; // match mixed chart legend spacing
    const paddingForEdges = 40;
    const totalLabelChars = this.series.reduce((acc, s) => acc + (s.name ? s.name.length : 0), 0);
    const estimatedLegendWidth = this.series.length * (itemWidthPx + itemGap) + totalLabelChars * avgCharWidth + paddingForEdges;
    const legendIsCentered = estimatedLegendWidth < chartWidth;
    const legendObj: any = {
      show: this.showLegend,
      type: 'scroll',
      orient: 'horizontal',
      top: '5%',
      textStyle: { fontSize: 11, color: theme.chart?.legendTextColor || '#333' },
      itemGap: itemGap,
      itemWidth: itemWidthPx,
      itemHeight: itemWidthPx
    };
    if (legendIsCentered) {
      legendObj.left = 'center';
    } else {
      legendObj.right = 10;
    }
    // Allow dynamic primary legend from chart config
    const preferredLegendKey: string | null = this.data?.chartConfig?.primaryLegend || null;
    const preferredLegendColor: string | null = this.data?.chartConfig?.primaryLegendColor || null;

    const legendData = this.series.map(s => s && s.name ? s.name : '');
    if (preferredLegendKey) {
      const idx = legendData.findIndex(n => (n || '').toLowerCase() === (preferredLegendKey || '').toLowerCase());
      if (idx > 0) {
        const foundName = legendData.splice(idx, 1)[0];
        legendData.unshift(foundName);
      }
    } else {
      // Backward-compat: if no preferredLegendKey, keep previous special-case for 'cummins'
      const cumIdx = legendData.findIndex(n => (n || '').toLowerCase() === 'cummins');
      if (cumIdx > 0) {
        const cumName = legendData.splice(cumIdx, 1)[0];
        legendData.unshift(cumName);
      }
    }

    if (legendData.length) {
      legendObj.data = legendData;
      // Reorder series to match legend order so colors and stacking align
      const orderedSeries: StackedBarSeries[] = [];
      legendData.forEach(name => {
        const found = this.series.find(s => s && s.name === name);
        if (found) orderedSeries.push(found);
      });
      // Append any missing series
      this.series.forEach(s => { if (!orderedSeries.includes(s)) orderedSeries.push(s); });
      this.series = orderedSeries;
    }
    // Color selection: support chartConfig.topNColorsByStackPosition and chartConfig.colorMap
    const defaultPalette = ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc'];
    const topNColors: string[] = this.data?.chartConfig?.topNColorsByStackPosition || [];
    const colorMap: { [k: string]: string } = this.data?.chartConfig?.colorMap || {};
    const colors = this.data?.colors || defaultPalette;
    const getSeriesColor = (name: string, idx: number) => {
      // If a preferred legend is set and matches this series, honor its configured color
      if (preferredLegendKey && (name || '').toLowerCase() === preferredLegendKey.toLowerCase()) {
        return preferredLegendColor || '#dc2e2e';
      }
      // Backward-compat: default cummins to dark red when no preferredLegendKey specified
      if (!preferredLegendKey && (name || '').toLowerCase() === 'cummins') return '#8B0000';
      if (topNColors && topNColors.length > idx && topNColors[idx]) return topNColors[idx];
      if (colorMap && colorMap[name]) return colorMap[name];
      return colors[idx % colors.length];
    };

    // Ensure each series has a color set
    this.series = this.series.map((s, i) => ({ ...s, color: s.color || getSeriesColor(s.name, i) }));

    const normalizedSeries = this.normalizeSeries(this.series);

    const minLabelThreshold = this.data?.chartConfig?.minLabelThreshold ?? 20;

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: theme.tooltip?.bg || '#fff',
        borderColor: theme.tooltip?.borderColor || '#ccc',
        textStyle: {
          color: theme.tooltip?.textColor || '#333',
          fontSize: theme.tooltip?.fontSize || 13,
        },
        formatter: (params: any) => {
          // params is an array of series items for the hovered category
          if (!Array.isArray(params) || params.length === 0) return '';
          const humanize = (s: any) => {
            if (s === null || s === undefined) return '';
            return String(s)
              .replace(/_/g, ' ')
              .split(' ')
              .map((w: string) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
              .join(' ');
          };
          const catName = params[0]?.name || '';
          const quantityKey = this.data?.quantityKey;
          let out = `<div style="font-weight:600;margin-bottom:4px;">${humanize(catName)}</div>`;
          params.forEach((p: any) => {
            const seriesName = humanize(p.seriesName);
            const percent = p.value;
            const catIndex = p.dataIndex;
            const originalSeries = this.series.find(s => s.name === p.seriesName);
            const originalVal = originalSeries ? originalSeries.data[catIndex] : null;
            const formattedVal = originalVal == null ? '' : formatCompact(originalVal);
            // attempt to find raw record to surface quantity
            let qtySuffix = '';
            if (quantityKey && this.lastRawBar && this.lastRawBar.length) {
              const found = this.lastRawBar.find((r: any) => String(r[this.data.categoryKey]) === String(catName) && String(r[this.data.seriesKey]) === String(p.seriesName));
              if (found && found[quantityKey] !== undefined) {
                qtySuffix = ` (${humanize(quantityKey)}: ${fmtNumber(found[quantityKey])})`;
              }
            }
            out += `<div>${seriesName}: ${percent}%${formattedVal ? ` (${formattedVal})` : ''}</div>`;
            if (quantityKey && this.lastRawBar && this.lastRawBar.length) {
              const found = this.lastRawBar.find((r: any) => String(r[this.data.categoryKey]) === String(catName) && String(r[this.data.seriesKey]) === String(p.seriesName));
              if (found && found[quantityKey] !== undefined) {
                out += `<div style="margin-left:8px;">${humanize(quantityKey)}: ${fmtNumber(found[quantityKey])}</div>`;
              }
            }
          });
          return out;
        }
      },
      legend: legendObj,
      grid: {
        containLabel: true
      },
      xAxis: {
        type: 'value',
        max: 100,
        axisLabel: { formatter: '{value}%' }
      },
      yAxis: {
        type: 'category',
        data: this.categories
      },
      series: normalizedSeries.map(s => ({
        name: s.name,
        type: 'bar',
        stack: 'total',
        label: {
          show: true,
          formatter: (params: any) => {
            const v = params?.value;
            if (v === 0 || v === null || v === undefined) return '';
            const num = typeof v === 'number' ? v : Number(v);
            return isNaN(num) ? '' : (num < minLabelThreshold ? '' : `${num}%`);
          }
        },
        itemStyle: {
          color: s.color
        },
        data: s.data
      }))
    };

    console.log('Stacked100Bar: Setting chart option', option);

    // Ensure legend supports selection and attach exclusive-selection handler
    try {
      this.chart.setOption({ legend: { selectedMode: true } });
      try { this.chart.off('legendselectchanged'); } catch (e) { /* ignore */ }
      this.chart.on('legendselectchanged', (params: any) => {
        const name: string = params && params.name;
        const seriesNames: string[] = (this.series || []).map((s: any) => s && s.name).filter(Boolean as any);
        if (!seriesNames.length || !name) return;

        // Apply after microtask to avoid racing with ECharts internal toggles
        setTimeout(() => {
          if (this.exclusiveLegendName !== name) {
            this.exclusiveLegendName = name;
            const explicit: { [k: string]: boolean } = {};
            seriesNames.forEach(n => explicit[n] = (n === name));
            this.chart.setOption({ legend: { selected: explicit } });
            seriesNames.forEach(n => this.chart.dispatchAction({ type: n === name ? 'legendSelect' : 'legendUnSelect', name: n }));
          } else {
            this.exclusiveLegendName = null;
            const allSelected: { [k: string]: boolean } = {};
            seriesNames.forEach(n => allSelected[n] = true);
            this.chart.setOption({ legend: { selected: allSelected } });
            seriesNames.forEach(n => this.chart.dispatchAction({ type: 'legendSelect', name: n }));
          }
        }, 0);
      });
    } catch (e) {
      // ignore legend wiring errors
    }

    applyRoleVisibilityToOption(option, this.data);

    this.chart.setOption(option, true);
    // Ensure chart is resized to current container size
    setTimeout(() => this.chart?.resize(), 0);
    this.setupChartClickEvent();
    this.chart.hideLoading();
    console.log('Stacked100Bar: Chart rendered successfully');
  }

  protected setupChartClickEvent(): void {
    this.chart.off('click');
    this.chart.on('click', (params: any) => {
      if (!params.name) return;

      const clickedCategory = params.name;
      const clickedSeriesName = params.seriesName;

      // Toggle: click same data again → reset
      if (this.selectedCategory === clickedCategory && this.selectedSeriesName === clickedSeriesName) {
        this.selectedSeriesName = null;
        this.selectedCategory = null;
        // this.chartFilterHandler.resetFilterSelection(this.data);
        // Reload self with current filters (chart-click filters removed)
        this.loadAndRenderChart(this.globalFilterService.getCurrentFilters());
        return;
      }

      this.selectedCategory = clickedCategory;
      this.selectedSeriesName = clickedSeriesName;

      // Build values array matching filterKey order
      const filterKeys = (this.data.filterKey || '').split(',').map((k: string) => k.trim());
      const values: string[] = [];
      
      // For each filter key, try to find the corresponding value
      filterKeys.forEach((key: string) => {
        // First try to match against the clicked category (the y-axis label)
        if (key === this.data.categoryKey) {
          values.push(clickedCategory);
        } 
        // If there's a seriesKey and it matches, use the clicked series name
        else if (this.data.seriesKey && key === this.data.seriesKey) {
          values.push(clickedSeriesName || '');
        }
        else {
          // For keys we don't have direct mapping, push empty string
          // The backend might handle this, or we need additional data
          values.push('');
        }
      });

      this.chartFilterHandler.updateFilterSelection(this.data, values);
      // Reload self with updated filters to show only clicked data
      this.loadAndRenderChart(this.globalFilterService.getCurrentFilters());
    });
  }

  /** Converts raw values into 100% stacked values */
  protected normalizeSeries(series: StackedBarSeries[]): StackedBarSeries[] {
    const length = series[0].data.length;
    const totals = new Array(length).fill(0);

    series.forEach(s =>
      s.data.forEach((v, i) => (totals[i] += v))
    );

    return series.map(s => ({
      ...s,
      data: s.data.map((v, i) =>
        totals[i] ? +(v / totals[i] * 100).toFixed(1) : 0
      )
    }));
  }

  protected transformData(
    rawData: any[],
    categoryKey: string,
    seriesKey: string,
    valueKey: string,
  ): { category: string[]; series: StackedBarSeries[] } {
    console.log('Stacked100Bar: transformData called with keys:', categoryKey, seriesKey, valueKey);
    const categories = [...new Set(rawData.map((d) => d[categoryKey]))].sort();
    const seriesNames = [...new Set(rawData.map((d) => d[seriesKey]))];
    const series = seriesNames.map((name) => {
      const data = categories.map((cat) => {
        const items = rawData.filter((d) => d[categoryKey] === cat && d[seriesKey] === name);
        return items.reduce((sum, item) => sum + (item[valueKey] || 0), 0);
      });
      return { name, data };
    });
    return { category: categories, series };
  }

  protected updateSlicedData(): void {
    if (this.allCategories.length > this.maxVisibleCategories) {
      const end = Math.min(this.sliderIndex + this.maxVisibleCategories, this.allCategories.length);
      this.categories = this.allCategories.slice(this.sliderIndex, end);
      this.series = this.allSeries.map(s => ({
        ...s,
        data: s.data.slice(this.sliderIndex, end)
      }));
    } else {
      this.categories = this.allCategories;
      this.series = this.allSeries;
    }
  }

  previousSlide(): void {
    if (this.sliderIndex > 0) {
      this.sliderIndex--;
      this.updateSlicedData();
      this.updateChart();
    }
  }

  nextSlide(): void {
    if (this.sliderIndex + this.maxVisibleCategories < this.allCategories.length) {
      this.sliderIndex++;
      this.updateSlicedData();
      this.updateChart();
    }
  }

  protected getResolvedHeight(): string {
    return this.responsiveLayoutService.resolveWidgetHeight(this.data);
  }

  protected syncVisibleHeightFromConfig(): void {
    const resolvedHeight = this.responsiveLayoutService.resolveHeightToPixels(this.data);
    if (resolvedHeight && resolvedHeight > 0) {
      this.visibleHeight = resolvedHeight;
      return;
    }

    this.syncVisibleHeightFromContainer();
  }

  protected syncVisibleHeightFromContainer(): void {
    const containerHeight = this.chartContainer?.nativeElement?.clientHeight;
    if (containerHeight && containerHeight > 0) {
      this.visibleHeight = containerHeight;
    }
  }

  onScrollbarMouseDown(event: MouseEvent): void {
    event.preventDefault();
    if (this.allCategories.length <= this.maxVisibleCategories) return;
    this.isScrollbarDragging = true;
    this.scrollbarStartY = event.clientY;
    this.scrollStartOffset = this.sliderIndex;
    this.dragHandler = (e: MouseEvent) => this.onScrollbarDrag(e);
    this.upHandler = () => this.onScrollbarMouseUp();
    document.addEventListener('mousemove', this.dragHandler!);
    document.addEventListener('mouseup', this.upHandler!);
  }

  getScrollbarThumbHeight(): number {
    if (this.allCategories.length <= this.maxVisibleCategories) return 0;
    return (this.maxVisibleCategories / this.allCategories.length) * this.visibleHeight;
  }

  getScrollbarThumbTop(): number {
    if (this.allCategories.length <= this.maxVisibleCategories) return 0;
    const maxScroll = this.allCategories.length - this.maxVisibleCategories;
    const scrollableHeight = this.visibleHeight;
    const thumbHeight = this.getScrollbarThumbHeight();
    const maxThumbTravel = scrollableHeight - thumbHeight;
    return (this.sliderIndex / maxScroll) * maxThumbTravel;
  }

  protected onScrollbarDrag(event: MouseEvent): void {
    if (!this.isScrollbarDragging) return;
    const deltaY = event.clientY - this.scrollbarStartY;
    const maxScroll = this.allCategories.length - this.maxVisibleCategories;
    const scrollableHeight = this.visibleHeight;
    const thumbHeight = (this.maxVisibleCategories / this.allCategories.length) * scrollableHeight;
    const maxThumbTravel = scrollableHeight - thumbHeight;
    if (maxThumbTravel <= 0) return;
    const startThumbY = (this.scrollStartOffset / Math.max(1, maxScroll)) * maxThumbTravel;
    const newThumbY = startThumbY + deltaY;
    const clampedThumbY = Math.max(0, Math.min(newThumbY, maxThumbTravel));
    const newOffset = Math.round((clampedThumbY / maxThumbTravel) * maxScroll);
    if (newOffset !== this.sliderIndex && newOffset >= 0 && newOffset <= maxScroll) {
      this.sliderIndex = newOffset;
      this.updateSlicedData();
      this.updateChart();
    }
  }

  protected onScrollbarMouseUp(): void {
    this.isScrollbarDragging = false;
    if (this.dragHandler) {
      document.removeEventListener('mousemove', this.dragHandler);
      this.dragHandler = null;
    }
    if (this.upHandler) {
      document.removeEventListener('mouseup', this.upHandler);
      this.upHandler = null;
    }
  }
}

